import { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';

interface CropToolProps {
  src: string;
  onSave: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

/**
 * CropTool — wraps react-image-crop (same library & pattern as ImgDropzone).
 *
 * Flow:
 *   1. ReactCrop renders the image + interactive handles.
 *   2. onImageLoaded captures imgRef so we know natural dimensions.
 *   3. Every time the user finishes a drag, onComplete fires → completedCrop.
 *   4. useEffect redraws a hidden canvas from completedCrop + imgRef.
 *   5. handleSave calls canvas.toBlob → onSave(blob).
 */
function CropTool({ src, onSave, onCancel }: CropToolProps) {
  const { t } = useTranslation('assetLibraryAssets');

  /** Ref to the <img> that ReactCrop renders internally */
  const imgRef = useRef<HTMLImageElement | null>(null);
  /** Hidden canvas — only used to produce the pixel-accurate cropped output */
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Remote (S3) sources must be requested in CORS mode, otherwise the browser taints the export
   * canvas and `toBlob` throws a SecurityError. `blob:`/`data:` sources are same-origin already
   * and setting the attribute on them is unnecessary.
   */
  const imageCrossOrigin: 'anonymous' | undefined = /^(blob:|data:)/.test(src) ? undefined : 'anonymous';

  /** Interactive crop state (percentage-based initial region) */
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10,
  });

  /** Pixel-unit snapshot of the crop, set when the user releases the mouse */
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);

  /** Capture the HTMLImageElement that ReactCrop renders. */
  const onImageLoaded = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;
  }, []);

  /**
   * Re-draw the hidden canvas whenever completedCrop changes.
   *
   * `completedCrop` is reported in *displayed* pixels, so the canvas is sized
   * from the natural-resolution source rectangle instead — same approach as the
   * natural-resolution branch of ImgDropzone.js. Sizing it in displayed pixels
   * would downscale every crop to whatever the on-screen preview happened to be.
   */
  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    /**
     * Destructure and guard: onComplete always supplies all four fields in px
     * but the Crop type marks them optional — narrow here to satisfy TS.
     */
    const { x, y, width, height } = completedCrop;
    if (x == null || y == null || width == null || height == null) return;

    const sourceWidth = width * scaleX;
    const sourceHeight = height * scaleY;

    canvas.width = Math.round(sourceWidth);
    canvas.height = Math.round(sourceHeight);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, x * scaleX, y * scaleY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);
  }, [completedCrop]);

  /** Export the canvas contents as a Blob and pass it to the parent. */
  const handleSave = () => {
    const canvas = previewCanvasRef.current;
    if (!canvas || !completedCrop?.width || !completedCrop?.height) return;

    canvas.toBlob(
      (blob) => {
        if (blob) onSave(blob);
      },
      'image/jpeg',
      0.92,
    );
  };

  return (
    <div className="crop-tool" data-testid="crop-tool">
      <div className="ium-upload-panel">
        <div className="ium-dropzone-wrap">
          {/* react-image-crop renders its own <img> + SVG handles + scrim */}
          <ReactCrop
            src={src}
            crossorigin={imageCrossOrigin}
            onImageLoaded={onImageLoaded}
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
          />

          {/* Hidden canvas — pixel-accurate cropped output, never shown directly */}
          <canvas ref={previewCanvasRef} className="d-none" />
        </div>
      </div>
      <div className="crop-tool__actions">
        <button type="button" className="bombiix-btn btn-light" onClick={onCancel}>
          {t('assets.cancel')}
        </button>
        <button
          type="button"
          className="bombiix-btn btn-blue"
          onClick={handleSave}
          disabled={!completedCrop?.width || !completedCrop?.height}
          data-testid="crop-save"
        >
          {t('assets.cropImage')}
        </button>
      </div>
    </div>
  );
}

export default CropTool;
